import { useQuery } from "@tanstack/react-query";
import { getHomepageAccommodations } from "../api/accommodation.api";
import type { AccommodationWithDetails } from "../types/accommodation.types";

export const useRecommendedAccommodations = () => {
  return useQuery({
    queryKey: ["recommended-accommodations"],
    queryFn: async () => {
      const allAccommodations = await getHomepageAccommodations();

      // Shuffle helper
      const shuffle = <T,>(array: T[]): T[] => {
        const shuffled = [...array];
        for (let i = shuffled.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
        }
        return shuffled;
      };

      // Group by trail_name to ensure diversity
      const byTrail = allAccommodations.reduce((acc, accommodation) => {
        const trail = accommodation.trail_name || 'Unknown';
        if (!acc[trail]) acc[trail] = [];
        acc[trail].push(accommodation);
        return acc;
      }, {} as Record<string, AccommodationWithDetails[]>);

      // Shuffle accommodations within each trail
      const trails = Object.keys(byTrail);
      trails.forEach(trail => {
        byTrail[trail] = shuffle(byTrail[trail]);
      });

      // Shuffle trail order to randomize which trails get picked first
      const shuffledTrails = shuffle(trails);

      // Select up to 3 accommodations, max 1 per trail
      const selected: AccommodationWithDetails[] = [];
      for (const trail of shuffledTrails) {
        if (selected.length >= 3) break;

        // Prefer partners first, then featured
        const trailAccommodations = byTrail[trail];
        const partner = trailAccommodations.find(acc => acc.is_partner);
        const featured = trailAccommodations.find(acc => acc.featured && !acc.is_partner);

        const pick = partner || featured || trailAccommodations[0];
        if (pick) selected.push(pick);
      }

      return selected;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};
